import React, {useState, useEffect, useMemo} from 'react';
import {
    Button,
    Table,
    Input,
    Form,
    Select,
    Typography,
    message,
    Layout,
    Row,
    Col,
    Card,
    Space,
    Modal,
    Transfer,
    Popconfirm,
    Radio, RadioChangeEvent, Dropdown
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    EditOutlined,
    TeamOutlined,
    PlusOutlined,
    DownloadOutlined, DownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    DropdownResponse,
    EventCreateEventMembersRequest,
    EventCreateEventsRequest,
    EventCreateItem, EventDeleteEventMembersRequest,
    EventDeleteEventsRequest, EventGetCandidateMembersRequest,
    EventGetEventMembersRequest,
    EventGetEventRequest,
    EventGetEventResponseItem,
    EventMemberCreateItem, EventMemberDeleteItem,
    EventSearchRequest,
    EventSearchResponseItem,
    EventUpdateEventsRequest
} from "../../model";
import {baseDataService, eventService, useAuth} from "../../services";
import {getEditableRoles, getPrintableRoles} from "../../utils/rolePermissions";
import {useLocation} from "react-router-dom";
import EventPdfViewer from "./components/EventPdfViewer";
import {reportService} from "../../services/domain/report/service";
import {IdentityTypeModalPops} from "../../model/event/ui/pdf-viewer-props";
//import ROCDatePicker from "../../components/ROCDatePicker";
import {toROCDate} from "../../utils/DateUtil";
import RocDateInputPicker from "../../components/shared/RocDateInputPicker";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const {Title} = Typography;
const {Option} = Select;
const {Content} = Layout;
const {TextArea} = Input;

const EventPage: React.FC = () => {
    const {hasEditPermission, hasPrintPermission} = useAuth();
    const location = useLocation();
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [searchForm] = Form.useForm();
    const [createForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState<EventSearchResponseItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [eventTypeOptions, setEventTypeOptions] = useState<DropdownResponse[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // Edit Modal States
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [, setEditingEvent] = useState<EventSearchResponseItem | null>(null);
    const [editLoading, setEditLoading] = useState(false);

    // Create Modal States
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // Member Assignment Modal States
    const [memberModalVisible, setMemberModalVisible] = useState(false);
    const [currentEvent, setCurrentEvent] = useState<EventGetEventResponseItem | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // Transfer component states
    const [transferData, setTransferData] = useState<any[]>([]);
    const [targetKeys, setTargetKeys] = useState<string[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    const [selectedMembershipIds, setSelectedMembershipIds] = useState<number[]>([]);
    // const [identityTypeModalVisible, setIdentityTypeModalVisible] = useState(false);
    const [identityTypeModalPops, setIdentityTypeModalProps] = useState<IdentityTypeModalPops>({
        actionKey: '',
        visible: false
    });
    const [printLanguage, setPrintLanguage] = useState("ZH_TW");
    const [previewPdfLanguageModalVisible, setPreviewPdfLanguageModalVisible] = useState(false);
    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
    const identityTypeOptions = [
        {label: '負責人', value: 'RESPONSIBLE_PERSON'},
        {label: '董事長', value: 'CHAIRMAN'},
        {label: '總經理', value: 'GENERAL_MANAGER'},
        {label: '會籍聯絡人', value: 'MEMBERSHIP_CONTACT'},
        {label: '專委會聯絡人', value: 'COMMITTEE_CONTACT'},
        {label: '展覽會聯絡人', value: 'EXHIBITION_CONTACT'}
    ];
    const [identityType, setIdentityType] = useState(identityTypeOptions[0]?.value || '');


    // 初始加載
    useEffect(() => {
        // 載入活動類別選項
        loadEventTypeOptions();

        // 初始搜尋所有活動
        // handleSearch({});
    }, []);

    // 載入活動類別選項
    const loadEventTypeOptions = async () => {
        setIsLoadingOptions(true);
        try {
            const result = await baseDataService.getDropdown({
                paramType: 'event_type'
            });

            if (result && result.length > 0) {
                setEventTypeOptions(result);
            } else {
                message.error('載入活動類別選項失敗');
            }
        } catch (error) {
            console.error('載入活動類別選項失敗:', error);
            message.error('載入活動類別選項失敗');
        } finally {
            setIsLoadingOptions(false);
        }
    };

    // 搜尋活動
    const handleSearch = async (values: any) => {
        setLoading(true);
        try {
            const [start, end] = values.eventDateRange || [];
            // 構建搜尋請求 - 確保所有欄位為空時傳送空字串而非 undefined
            // const searchRequest: EventSearchRequest = {
            //     eventType: values.eventType || '',
            //     eventName: values.eventName || '',
            //     startDate: values.eventDateRange ? values.eventDateRange[0].format('YYYY-MM-DD') : '',
            //     endDate: values.eventDateRange ? values.eventDateRange[1].format('YYYY-MM-DD') : '',
            // };
            const searchRequest: EventSearchRequest = {
                eventType: values.eventType || '',
                eventName: values.eventName || '',
                startDate: start ? start.format('YYYY-MM-DD') : '',
                endDate: end ? end.format('YYYY-MM-DD') : '',
            };

            // 調用API搜尋活動
            const result = await eventService.searchEvents(searchRequest);

            if (result) {
                setEvents(result);
                setTotalRecords(result.length);
                // 重置到第一頁
                setPagination(resetPaginationByTotal(result.length));

                // 如果沒有返回數據，顯示通知
                if (result.length === 0) {
                    message.info('查無資料');
                }
            } else {
                // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
                setEvents([]);
                setTotalRecords(0);
                message.error('查詢失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋活動失敗:', error);
            message.error('搜尋失敗，請稍後重試');
            setEvents([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // 處理分頁、篩選和排序變更
    const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
        const newPagination = {
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        };

        setPagination(newPagination);
    };

    // 重置表單
    const handleReset = () => {
        form.resetFields();
    };

    // 開始編輯事件
    const handleEdit = async (record: EventSearchResponseItem) => {
        setEditingEvent(record);
        setEditLoading(true);

        try {
            // 先從API獲取完整的活動數據
            const getEventRequest: EventGetEventRequest = {
                eventId: record.eventId
            };

            const eventData = await eventService.getEvent(getEventRequest);

            if (eventData) {
                // 填充表單數據
                editForm.setFieldsValue({
                    eventId: eventData.eventId,
                    eventType: eventData.eventType,
                    eventName: eventData.eventName,
                    startDate: eventData.startDate ? dayjs(eventData.startDate) : null,
                    endDate: eventData.endDate ? dayjs(eventData.endDate) : null
                });

                setEditModalVisible(true);
            } else {
                message.error('無法獲取活動詳細資料');
            }
        } catch (error) {
            console.error('獲取活動詳細資料失敗:', error);
            message.error('獲取活動詳細資料失敗，請稍後重試');

            // 發生錯誤時，仍然使用當前數據填充表單
            editForm.setFieldsValue({
                eventId: record.eventId,
                eventType: record.eventType,
                eventName: record.eventName,
                startDate: record.startDate ? dayjs(record.startDate) : null,
                endDate: record.endDate ? dayjs(record.endDate) : null
            });

            setEditModalVisible(true);
        } finally {
            setEditLoading(false);
        }
    };

    // 提交編輯事件
    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validateFields();
            setEditLoading(true);

            const updateRequest: EventUpdateEventsRequest = {
                events: [{
                    eventId: values.eventId,
                    eventType: values.eventType,
                    eventName: values.eventName,
                    startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
                    endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
                    modifiedBy: 'web_user' // 可根據實際登入用戶調整
                }]
            };

            const result = await eventService.updateEvent(updateRequest);

            if (result) {
                message.success('更新成功');
                setEditModalVisible(false);
                // 刷新列表
                handleSearch(form.getFieldsValue());
            } else {
                message.error('更新失敗');
            }
        } catch (error) {
            console.error('更新活動失敗:', error);
            message.error('更新失敗，請稍後重試');
        } finally {
            setEditLoading(false);
        }
    };

    // 關閉編輯模態框
    const handleEditCancel = () => {
        setEditModalVisible(false);
        setEditingEvent(null);
    };

    // 顯示新增活動對話框
    const showCreateModal = () => {
        createForm.resetFields();
        setCreateModalVisible(true);
    };

    // 關閉新增活動對話框
    const handleCreateCancel = () => {
        setCreateModalVisible(false);
    };

    // 提交新增活動
    const handleCreateSubmit = async () => {
        try {
            const values = await createForm.validateFields();
            setCreateLoading(true);

            // 構建新增請求
            const createItem: EventCreateItem = {
                eventType: values.eventType,
                eventName: values.eventName,
                startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
                endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
                remarks: values.remarks,
                createdBy: 'web_user' // 可根據實際登入用戶調整
            };

            const createRequest: EventCreateEventsRequest = {
                events: [createItem]
            };

            const result = await eventService.createEvent(createRequest);

            if (result) {
                message.success(`成功新增 ${result.createdCount} 筆活動`);
                setCreateModalVisible(false);
                console.log(JSON.stringify(form.getFieldsValue()));
                // 刷新列表
                handleSearch(form.getFieldsValue());
            } else {
                message.error('新增活動失敗');
            }
        } catch (error) {
            console.error('新增活動失敗:', error);
            message.error('新增失敗，請稍後重試');
        } finally {
            setCreateLoading(false);
        }
    };

    // 刪除事件
    const confirmDelete = async (record: EventSearchResponseItem) => {
        try {
            const deleteRequest: EventDeleteEventsRequest = {
                eventIds: [{eventId: record.eventId}]
            };

            const result = await eventService.deleteEvent(deleteRequest);

            if (result) {
                message.success(`成功刪除 ${result.deletedCount} 筆資料`);
                // 刷新列表
                handleSearch(form.getFieldsValue());
            } else {
                message.error('刪除失敗');
            }
        } catch (error) {
            console.error('刪除活動失敗:', error);
            message.error('刪除失敗，請稍後重試');
        }
    };

    // 處理查看活動詳情和會員指派
    const handleViewDetail = async (record: EventSearchResponseItem) => {
        setDetailLoading(true);
        try {
            // 先取得活動詳細資料
            const getEventRequest: EventGetEventRequest = {
                eventId: record.eventId
            };

            const eventData = await eventService.getEvent(getEventRequest);

            if (eventData) {
                setCurrentEvent(eventData);
                setMemberModalVisible(true);

                // 重置搜尋表單
                searchForm.resetFields();

                // 獲取活動已分配的會員
                const eventMembersRequest: EventGetEventMembersRequest = {
                    eventId: record.eventId
                };

                const eventMembers = await eventService.getEventMembers(eventMembersRequest);

                if (eventMembers) {
                    // 設置 Transfer 的右側面板數據（已分配會員）
                    setTransferData([
                        // 已分配的會員加入，以便顯示在右側面板
                        ...eventMembers.map(member => ({
                            key: member.eventMemberId.toString(),
                            title: `${member.memberId} - ${member.companyName}`,
                            description: '',
                            membershipId: member.membershipId,
                            memberId: member.memberId,
                            companyName: member.companyName
                        }))
                    ]);

                    // 設置已分配的會員至右側面板
                    setTargetKeys(eventMembers.map(member =>
                        member.eventMemberId.toString()
                    ));

                    setSelectedKeys([]);
                } else {
                    setTransferData([]);
                    setTargetKeys([]);
                    message.error('無法載入活動會員');
                }
            } else {
                message.error('無法載入活動資料');
            }
        } catch (error) {
            console.error('載入活動資料失敗:', error);
            message.error('載入活動資料失敗，請稍後重試');
        } finally {
            setDetailLoading(false);
        }
    };

    // 關閉詳情對話框
    const handleDetailCancel = () => {
        setMemberModalVisible(false);
        setCurrentEvent(null);
    };

    // 處理會員轉移
    const handleMembershipChange = async (newTargetKeys: React.Key[], direction: string, moveKeys: React.Key[]) => {
        // 更新右側面板的已選會員
        setTargetKeys(newTargetKeys as string[]);

        if (!currentEvent) return;

        try {
            // 如果是新增會員 (從左到右)
            if (direction === 'right' && moveKeys.length > 0) {
                setSaveLoading(true);

                // 獲取要新增的會員資料
                const membersToAdd: EventMemberCreateItem[] = transferData
                    .filter(item => moveKeys.includes(item.key))
                    .map(item => ({
                        eventId: currentEvent.eventId,
                        membershipId: item.membershipId,
                        createdBy: 'web_user'
                    }));

                // 呼叫API新增會員
                const createRequest: EventCreateEventMembersRequest = {
                    eventMembers: membersToAdd
                };

                const result = await eventService.createEventMembers(createRequest);

                if (result) {
                    message.success(`已成功新增 ${result.createdCount} 位會員`);

                    // 重新載入活動會員以獲取正確的 eventMemberId
                    await handleViewDetail({
                        eventId: currentEvent.eventId,
                        eventType: currentEvent.eventType,
                        eventTypeName: eventTypeOptions.find(o => o.paramCode === currentEvent.eventType)?.paramValue || currentEvent.eventType,
                        eventName: currentEvent.eventName,
                        startDate: currentEvent.startDate,
                        endDate: currentEvent.endDate
                    });
                } else {
                    message.error('新增會員失敗');
                }
            }
            // 如果是移除會員 (從右到左)
            else if (direction === 'left' && moveKeys.length > 0) {
                setSaveLoading(true);

                // 獲取要移除的會員 ID
                const membersToRemove: EventMemberDeleteItem[] = moveKeys.map(key => ({
                    eventMemberId: parseInt(key.toString(), 10)
                }));

                // 呼叫API刪除會員
                const deleteRequest: EventDeleteEventMembersRequest = {
                    eventMemberIds: membersToRemove
                };

                const result = await eventService.deleteEventMembers(deleteRequest);

                if (result) {
                    message.success(`已成功移除 ${result.deletedCount} 位會員`);

                    // 重新載入活動會員
                    await handleViewDetail({
                        eventId: currentEvent.eventId,
                        eventType: currentEvent.eventType,
                        eventTypeName: eventTypeOptions.find(o => o.paramCode === currentEvent.eventType)?.paramValue || currentEvent.eventType,
                        eventName: currentEvent.eventName,
                        startDate: currentEvent.startDate,
                        endDate: currentEvent.endDate
                    });
                } else {
                    message.error('移除會員失敗');
                }
            }
        } catch (error) {
            console.error('更新活動會員失敗:', error);
            message.error('操作失敗，請稍後重試');
        } finally {
            setSaveLoading(false);
        }
    };

    // 處理會員選擇
    const handleMembershipSelectChange = (sourceSelectedKeys: React.Key[], targetSelectedKeys: React.Key[]) => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys] as string[]);
    };

    // 處理列印功能
    const handleTest = (actionKey: string) => {
        // 獲取已選中的委員會會員IDs (選中的且已在右側的)
        const membershipIds = getSelectedMembershipIds();
        if (membershipIds.length === 0) {
            message.warning('請選擇至少一位會員');
            return;
        }
        // 設置選擇的會員ID並顯示PDF預覽器
        setSelectedMembershipIds(membershipIds);
        setIdentityTypeModalProps({
            actionKey: actionKey,
            visible: true
        });
    }

    // 執行候選會員搜尋，更新左側面板的數據
    const handleMemberSearch = async (values: any) => {
        if (!currentEvent) return;

        // 檢查是否至少提供了會員編號範圍或公司名稱之一
        if (!values.memberIdStart && !values.memberIdEnd && !values.companyName) {
            message.warning('請至少提供會員編號範圍或公司名稱');
            return;
        }

        setSearchLoading(true);
        try {
            // 構建候選會員搜尋請求
            const searchRequest: EventGetCandidateMembersRequest = {
                memberIdStart: values.memberIdStart,
                memberIdEnd: values.memberIdEnd,
                companyName: values.companyName
            };

            // 使用 getCandidateMembers API 取得符合條件的候選會員
            const candidateMembers = await eventService.getCandidateMembers(searchRequest);

            if (candidateMembers) {
                // 取得該活動目前的成員
                const eventMembersRequest: EventGetEventMembersRequest = {
                    eventId: currentEvent.eventId
                };

                const eventMembers = await eventService.getEventMembers(eventMembersRequest);

                if (eventMembers) {
                    // 取得已經分配的會員ID列表
                    const eventEventMemberIds = eventMembers.map(member => member.eventMemberId);
                    const eventMemberIds = eventMembers.map(member => member.memberId);

                    // 過濾出未分配的會員（客戶端過濾）
                    const availableMemberships = candidateMembers.filter(member =>
                        !eventMemberIds.includes(member.memberId)
                    );

                    // 設置 Transfer 的左側面板數據（可選會員）
                    const leftPaneData = availableMemberships.map(member => ({
                        key: member.membershipId.toString(),
                        title: `${member.memberId} - ${member.companyName}`,
                        description: '',
                        membershipId: member.membershipId,
                        memberId: member.memberId,
                        companyName: member.companyName
                    }));

                    // 右側面板的數據（已分配會員）
                    const rightPaneData = eventMembers.map(member => ({
                        key: member.eventMemberId.toString(),
                        title: `${member.memberId} - ${member.companyName}`,
                        description: '',
                        eventMemberId: member.eventMemberId,
                        memberId: member.memberId,
                        companyName: member.companyName
                    }));

                    // 合併左右側數據，用作 Transfer 的完整數據來源
                    setTransferData([...leftPaneData, ...rightPaneData]);

                    // 設置已分配的會員至右側面板
                    setTargetKeys(eventEventMemberIds.map(id => id.toString()));

                    // 如果沒有找到符合條件的未分配會員，顯示提示
                    if (availableMemberships.length === 0) {
                        message.info('查無符合條件的會員');
                    }
                }
            } else {
                message.error('搜尋候選會員失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋候選會員失敗:', error);
            message.error('搜尋候選會員失敗，請稍後重試');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleIdentityTypeChange = ({target: {value}}: RadioChangeEvent) => {
        setIdentityType(value);
    };

    // 表格列定義
    const columns = [
        {
            title: '活動類別',
            dataIndex: 'eventTypeName',
            key: 'eventTypeName',
            width: 120
        },
        {
            title: '活動名稱',
            dataIndex: 'eventName',
            key: 'eventName'
        },
        {
            title: '開始日期',
            dataIndex: 'startDate',
            key: 'startDate',
            width: 120,
            render: (text: string, record: EventSearchResponseItem) => {
                return toROCDate(text);
            }
        },
        {
            title: '結束日期',
            dataIndex: 'endDate',
            key: 'endDate',
            width: 120,
            render: (text: string, record: EventSearchResponseItem) => {
                return toROCDate(text);
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_: any, record: EventSearchResponseItem) => (
                <Space>
                    <Button
                        icon={<TeamOutlined/>}
                        onClick={() => handleViewDetail(record)}
                        title="指派會員"
                    >
                        指派會員
                    </Button>
                    <Button
                        icon={<EditOutlined/>}
                        onClick={() => handleEdit(record)}
                        title="編輯"
                        disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                    />
                    <Popconfirm
                        title="確定要刪除此活動嗎?"
                        onConfirm={() => confirmDelete(record)}
                        okText="確定"
                        cancelText="取消"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined/>}
                            title="刪除"
                            disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const getSelectedMembershipIds = () => {
        return selectedKeys
            .filter(key => targetKeys.includes(key.toString()))
            .map(key => {
                // 從transferData中找到對應的會員資料
                const memberData = transferData.find(item => item.key === key);
                return memberData ? memberData.membershipId : null;
            })
            .filter(id => id !== null) as number[]; // 過濾掉可能的null值
    }

    const apiParam = useMemo(() => {
        return {
            membershipIds: selectedMembershipIds.map(key => ({membershipId: key.toString()})),
            language: printLanguage,
            identityType: identityType
        };
    }, [selectedMembershipIds, printLanguage, identityType]);

    return (
        <Layout style={{minHeight: '100%'}}>
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" style={{marginBottom: 20}}>
                    <Col>
                        <Title level={3}>
                            <span style={{marginRight: 8}}>📅</span> 本會活動維護
                        </Title>
                    </Col>
                </Row>

                {/* 搜尋表單 */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined/>
                            <span>搜尋條件</span>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <Form
                        form={form}
                        name="eventSearch"
                        onFinish={handleSearch}
                        layout="vertical"
                    >
                        <Row gutter={[24, 0]}>
                            {/* 活動類別 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="活動類別" name="eventType">
                                    <Select
                                        placeholder="請選擇活動類別"
                                        loading={isLoadingOptions}
                                        allowClear
                                    >
                                        {eventTypeOptions.map(option => (
                                            <Option key={option.paramCode} value={option.paramCode}>
                                                {option.paramValue}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {/* 活動日期範圍 */}
                            {/*<Col xs={24} md={12}>*/}
                            {/*    <Form.Item label="活動日期範圍" name="eventDateRange">*/}
                            {/*        <RangePicker*/}
                            {/*            style={{width: '100%'}}*/}
                            {/*            locale={locale}*/}
                            {/*            format="YYYY-MM-DD"*/}
                            {/*        />*/}
                            {/*    </Form.Item>*/}
                            {/*</Col>*/}
                            <Col xs={24} md={12}>
                                <Form.Item label="活動日期範圍" required>
                                    <Row gutter={8}>
                                        <Col span={12}>
                                            <Form.Item name={["eventDateRange", 0]} noStyle>
                                                {/*<ROCDatePicker style={{ width: "100%" }} placeholder="開始日期" />*/}
                                                <RocDateInputPicker />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item shouldUpdate={(prev, cur) => prev.eventDateRange?.[0] !== cur.eventDateRange?.[0]} noStyle>
                                                {({ getFieldValue }) => {
                                                    //const startDate: Dayjs | null = getFieldValue(["eventDateRange", 0]) || null;
                                                    return (
                                                        <Form.Item name={["eventDateRange", 1]} noStyle>
                                                            <RocDateInputPicker />
                                                            {/*<ROCDatePicker*/}
                                                            {/*    style={{ width: "100%" }}*/}
                                                            {/*    placeholder="結束日期"*/}
                                                            {/*    disabledDate={(current: any) => {*/}
                                                            {/*        if (!startDate) return false;*/}
                                                            {/*        return current.isBefore(startDate, "day"); // 禁止選小於開始日期*/}
                                                            {/*    }}*/}
                                                            {/*/>*/}
                                                        </Form.Item>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form.Item>
                            </Col>

                            {/* 活動名稱 */}
                            <Col xs={24}>
                                <Form.Item label="活動名稱" name="eventName">
                                    <Input
                                        placeholder="輸入活動名稱"
                                        prefix={<span style={{marginRight: 6}}>🔖</span>}
                                    />
                                </Form.Item>
                            </Col>

                            {/* 按鈕區域 */}
                            <Col xs={24}>
                                <Row justify="center" gutter={[16, 0]}>
                                    <Col>
                                        <Button
                                            onClick={handleReset}
                                            icon={<DeleteOutlined/>}
                                        >
                                            清除
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SearchOutlined/>}
                                        >
                                            查詢
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* 搜尋結果區域 */}
                <Card
                    title={
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span>搜尋結果: {totalRecords} 筆記錄</span>
                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                onClick={() => showCreateModal()}
                                disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                            >
                                新增活動
                            </Button>
                        </div>
                    }
                >
                    <Table
                        virtual
                        scroll={{ y: 600 }}
                        rowKey="eventId"
                        dataSource={events}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            total: totalRecords,
                            showSizeChanger: true,
                            pageSizeOptions: getPageSizeOptions(totalRecords),
                            showTotal: (total) => `共 ${total} 筆`
                        }}
                        onChange={handleTableChange}
                    />
                </Card>

                {/* 編輯活動模態框 */}
                <Modal
                    title="編輯活動"
                    open={editModalVisible}
                    onCancel={handleEditCancel}
                    footer={[
                        <Button key="cancel" onClick={handleEditCancel}>
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={editLoading}
                            onClick={handleEditSubmit}
                        >
                            確定
                        </Button>,
                    ]}
                    width={800}
                >
                    <Form
                        form={editForm}
                        name="eventEdit"
                        layout="vertical"
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="eventId"
                                    label="活動ID"
                                    hidden
                                >
                                    <Input readOnly/>
                                </Form.Item>

                                <Form.Item
                                    name="eventType"
                                    label="活動類別"
                                    rules={[{required: true, message: '請選擇活動類別'}]}
                                >
                                    <Select
                                        placeholder="請選擇活動類別"
                                        loading={isLoadingOptions}
                                    >
                                        {eventTypeOptions.map(option => (
                                            <Option key={option.paramCode} value={option.paramCode}>
                                                {option.paramValue}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="eventName"
                                    label="活動名稱"
                                    rules={[{required: true, message: '請輸入活動名稱'}]}
                                >
                                    <Input placeholder="輸入活動名稱"/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item name="startDate" label="開始日期">
                                    <RocDateInputPicker />
                                </Form.Item>

                                <Form.Item name="endDate" label="結束日期">
                                    <RocDateInputPicker />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>

                {/* 指派會員模態框 */}
                <Modal
                    title={`活動會員管理: ${currentEvent?.eventName || ''}`}
                    open={memberModalVisible}
                    onCancel={handleDetailCancel}
                    footer={(
                        <Row justify="end" gutter={[8, 0]}>
                            <Col>
                                <Space.Compact>
                                    <Button onClick={handleDetailCancel}>
                                        關閉
                                    </Button>
                                    {selectedKeys.filter(key => targetKeys.includes(key)).length > 0 && (
                                        <>
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {
                                                            key: 'print',
                                                            label: '會員郵寄名條列表',
                                                            icon: (<DownloadOutlined/>)
                                                        }
                                                    ],
                                                    onClick: ({key}) => {
                                                        handleTest(key);
                                                    }
                                                }}
                                                disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                trigger={['click', 'hover']}
                                            >
                                                <Button
                                                    icon={<DownOutlined/>}
                                                    disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                >
                                                    列印選項
                                                </Button>
                                            </Dropdown>
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {
                                                            key: 'exportEventCsv',
                                                            label: '會員資料',
                                                            icon: (<DownloadOutlined/>)
                                                        },
                                                        {
                                                            key: 'exportEventTxt',
                                                            label: '電子郵件',
                                                            icon: (<DownloadOutlined/>)
                                                        },
                                                        {
                                                            key: 'exportEventFaxCsv',
                                                            label: '傳真',
                                                            icon: (<DownloadOutlined/>)
                                                        },
                                                    ],
                                                    onClick: ({key}) => {
                                                        handleTest(key);
                                                    }
                                                }}
                                                disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                trigger={['click', 'hover']}
                                            >
                                                <Button
                                                    icon={<DownOutlined/>}
                                                    disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                >
                                                    下載
                                                </Button>
                                            </Dropdown>
                                        </>
                                    )}

                                </Space.Compact>
                            </Col>
                        </Row>
                    )}
                    width={800}
                >
                    {detailLoading ? (
                        <div style={{textAlign: 'center', padding: '20px'}}>載入中...</div>
                    ) : (
                        <>
                            {/* 活動信息卡片 */}
                            {currentEvent && (
                                <Card
                                    size="small"
                                    style={{marginBottom: 16}}
                                >
                                    <Row gutter={[16, 8]}>
                                        <Col xs={24}>
                                            <div><strong>活動名稱:</strong> {currentEvent.eventName}</div>
                                        </Col>
                                    </Row>
                                    <Row gutter={[16, 8]}>
                                        <Col xs={24}>
                                            <div>
                                                <strong>活動類別:</strong>{' '}
                                                {eventTypeOptions.find(o => o.paramCode === currentEvent.eventType)?.paramValue || currentEvent.eventType}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={[16, 8]}>
                                        <Col xs={24}>
                                            <div>
                                                <strong>活動日期:</strong>{' '}
                                                {
                                                    (currentEvent?.startDate && currentEvent?.endDate)
                                                    ? `${toROCDate(currentEvent.startDate)} 至 ${toROCDate(currentEvent.endDate)}`
                                                    : currentEvent?.startDate
                                                        ? toROCDate(currentEvent.startDate)
                                                        : currentEvent?.endDate
                                                            ? toROCDate(currentEvent.endDate)
                                                            : '未設定'
                                                }
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                            {/* 搜尋表單 */}
                            <Card
                                title="搜尋會員"
                                size="small"
                                style={{marginBottom: 16}}
                            >
                                <Form
                                    form={searchForm}
                                    layout="vertical"
                                    onFinish={handleMemberSearch}
                                >
                                    <Row gutter={[16, 16]}>
                                        {/* 會員證號範圍 */}
                                        <Col xs={24} md={16}>
                                            <Form.Item label="會員證號範圍" style={{marginBottom: 0}}>
                                                <Input.Group compact>
                                                    <Form.Item
                                                        name="memberIdStart"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="起始證號"
                                                        />
                                                    </Form.Item>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '10%',
                                                        textAlign: 'center'
                                                    }}>~</span>
                                                    <Form.Item
                                                        name="memberIdEnd"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="結束證號"
                                                        />
                                                    </Form.Item>
                                                </Input.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* 公司名稱 */}
                                        <Col xs={24} md={8}>
                                            <Form.Item
                                                name="companyName"
                                                label="公司名稱"
                                            >
                                                <Input
                                                    placeholder="公司名稱關鍵字"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* 按鈕 */}
                                        <Col xs={24} style={{display: 'flex', justifyContent: 'flex-end'}}>
                                            <Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={searchLoading}
                                                    icon={<SearchOutlined/>}
                                                >
                                                    搜尋
                                                </Button>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card>

                            {/* 會員轉移組件 */}
                            <div>
                                <Transfer
                                    dataSource={transferData}
                                    titles={['符合搜尋條件的會員', '活動會員']}
                                    targetKeys={targetKeys}
                                    selectedKeys={selectedKeys}
                                    onChange={(newTargetKeys, direction, moveKeys) =>
                                        handleMembershipChange(newTargetKeys, direction, moveKeys)
                                    }
                                    onSelectChange={handleMembershipSelectChange}
                                    render={item => `${item.memberId} - ${item.companyName}`}
                                    listStyle={{
                                        width: 330,
                                        height: 400,
                                    }}
                                    filterOption={(inputValue, item) =>
                                        item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1 ||
                                        (item.description && item.description.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1)
                                    }
                                    showSearch
                                    locale={{
                                        searchPlaceholder: '搜尋會員資訊...',
                                        notFoundContent: '無符合條件的會員'
                                    }}
                                    disabled={saveLoading}
                                />
                            </div>
                        </>
                    )}
                </Modal>

                {/* 新增活動模態框 */}
                <Modal
                    title="新增活動"
                    open={createModalVisible}
                    onCancel={handleCreateCancel}
                    footer={[
                        <Button key="cancel" onClick={handleCreateCancel}>
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={createLoading}
                            onClick={handleCreateSubmit}
                        >
                            確定
                        </Button>,
                    ]}
                    width={800}
                >
                    <Form
                        form={createForm}
                        layout="vertical"
                        name="createEventForm"
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="eventType"
                                    label="活動類別"
                                    rules={[{required: true, message: '請選擇活動類別'}]}
                                >
                                    <Select
                                        placeholder="請選擇活動類別"
                                        loading={isLoadingOptions}
                                    >
                                        {eventTypeOptions.map(option => (
                                            <Option key={option.paramCode} value={option.paramCode}>
                                                {option.paramValue}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="eventName"
                                    label="活動名稱"
                                    rules={[{required: true, message: '請輸入活動名稱'}]}
                                >
                                    <Input placeholder="輸入活動名稱"/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item name="startDate" label="開始日期">
                                    <RocDateInputPicker />
                                </Form.Item>

                                <Form.Item name="endDate" label="結束日期">
                                    <RocDateInputPicker />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item name="remarks" label="備註">
                                    <TextArea rows={4} placeholder="活動備註"/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>

                <Modal
                    title="請選擇列印中文或英文"
                    open={previewPdfLanguageModalVisible}
                    onCancel={() => {
                        setPrintLanguage("EN_US");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onCancel')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    onOk={() => {
                        setPrintLanguage("ZH_TW");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onOk')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    okText="中文"
                    cancelText="英文"
                >
                </Modal>

                <Modal
                    title="請選擇身份別"
                    open={identityTypeModalPops.visible}
                    onCancel={() => {
                        setIdentityType(identityTypeOptions[0]?.value || '');
                        setIdentityTypeModalProps({
                            actionKey: '',
                            visible: false
                        })
                    }}
                    onOk={() => {
                        if (identityTypeModalPops.actionKey === 'print') {
                            setPreviewPdfLanguageModalVisible(true);
                        }

                        if (identityTypeModalPops.actionKey === 'exportEventCsv') {
                            reportService.exportEventCsv({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        }
                        if (identityTypeModalPops.actionKey === 'exportEventTxt') {
                            reportService.exportEventTxt({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        }

                        if (identityTypeModalPops.actionKey === 'exportEventFaxCsv') {
                            reportService.exportEventFaxCsv({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        }
                        setIdentityType(identityTypeOptions[0]?.value || '');
                        setIdentityTypeModalProps({
                            actionKey: '',
                            visible: false
                        })
                    }}
                >
                    <Radio.Group options={identityTypeOptions}
                                 onChange={handleIdentityTypeChange} value={identityType}/>
                </Modal>
            </Content>

            {/* PDF預覽器組件 */}
            {selectedKeys && (
                <EventPdfViewer
                    visible={pdfViewerVisible}
                    onClose={() => setPdfViewerVisible(false)}
                    apiParam={apiParam}
                />
            )}
        </Layout>
    );
};

export default EventPage;